import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { FadeUp, MaskedLines, Overline } from "@/components/Reveal";

const PROJECTS = [
  {
    id: "bushido",
    tag: "Proiect principal · anti-bullying",
    title: "Bushido vs. Bullying",
    lead:
      "Ateliere în școli în care copiii învață respect, curaj și stăpânire de sine prin codul samurailor — și prin care bullyingul își pierde publicul.",
    funds: [
      "Materiale pentru elevi: caiete de lucru, fișe și postere pentru clasă",
      "Deplasarea echipei la școli din Alba și din județele vecine",
      "Echipament pentru demonstrațiile practice de la ateliere",
      "Training și materiale pentru profesorii care continuă programul",
    ],
    to: "/bushido-vs-bullying",
    cta: "Vezi proiectul",
  },
  {
    id: "rise27",
    tag: "Eveniment · vara 2027",
    title: "RISE 27'",
    lead:
      "Festival pentru adolescenți și tineri la Cetatea Alba Carolina: muzică, artă urbană și istorie, cu acces gratuit pentru tinerii din programele noastre.",
    funds: [
      "Scenă, sonorizare și lumini pentru zilele de festival",
      "Ateliere creative: graffiti, foto, dans, producție muzicală",
      "Locuri gratuite pentru tinerii din centre și familii cu venituri mici",
      "Voluntari: transport, mese și echipament de identificare",
    ],
    to: "/rise27",
    cta: "Vezi festivalul",
  },
  {
    id: "cupa",
    tag: "Eveniment caritabil · Alba Iulia",
    title: "Cupa Caritabilă",
    lead:
      "Mini-campionat de fotbal cu echipe 8+1, unde fair-play-ul contează mai mult decât scorul, iar taxa de înscriere se întoarce în proiectele pentru copii.",
    funds: [
      "Închirierea terenului și arbitrajul meciurilor",
      "Mingi, veste, trusă medicală și apă pentru echipe",
      "Trofee și medalii pentru copii",
      "Un fond pentru participarea echipelor care nu își permit taxa",
    ],
    to: "/cupa-caritabila",
    cta: "Vezi competiția",
  },
  {
    id: "wallie",
    tag: "Proiect finalizat · 2018–2020",
    title: "Wallie Forest Kidz",
    lead:
      "Grădiniță în natură, unde copiii au învățat afară, în pădure, în orice anotimp. Proiectul s-a încheiat, dar metoda lui se regăsește în activitățile noastre outdoor.",
    funds: [
      "Experiența din acest proiect stă la baza activităților în aer liber de azi",
      "Nu mai primește finanțare — îl păstrăm aici pentru transparență",
    ],
    to: "/proiecte",
    cta: "Vezi toate proiectele",
  },
];

export default function Transparenta() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-28">
      <FadeUp>
        <Overline>Transparență</Overline>
      </FadeUp>
      <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-6xl">
        <MaskedLines lines={["Unde ajung", "banii"]} />
      </h1>
      <FadeUp delay={0.1}>
        <p
          data-testid="transparency-intro"
          className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-300"
        >
          Suntem o asociație mică, fără sedii scumpe și fără salarii mari. Fiecare
          formular 230, fiecare sponsorizare și fiecare donație intră direct în
          proiectele de mai jos. Mai jos scrie, concret, ce se plătește din ele.
        </p>
      </FadeUp>

      <div className="mt-20 space-y-8">
        {PROJECTS.map((p, i) => (
          <FadeUp key={p.id} delay={0.12 + i * 0.06}>
            <div
              data-testid={`transparency-project-${p.id}`}
              className="rounded-3xl border border-white/10 bg-surface p-7 sm:p-10"
            >
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-neon">
                {p.tag}
              </p>
              <h2 className="mt-4 font-display text-2xl font-black uppercase leading-tight tracking-tight text-white sm:text-3xl">
                {p.title}
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-300">{p.lead}</p>

              <div className="mt-8 grid gap-8 md:grid-cols-[1.2fr_0.8fr]">
                <div>
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
                    Ce se finanțează
                  </p>
                  <ul className="mt-4 space-y-3">
                    {p.funds.map((f) => (
                      <li key={f} className="flex gap-3 text-sm leading-relaxed text-neutral-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex items-end">
                  <Link
                    to={p.to}
                    data-testid={`transparency-link-${p.id}`}
                    className="group flex w-full items-center justify-between gap-3 rounded-full border border-white/15 px-6 py-3.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-neon hover:text-neon"
                  >
                    {p.cta}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>

      <FadeUp delay={0.2}>
        <div
          data-testid="transparency-cta"
          className="mt-20 rounded-3xl border border-neon/40 bg-neon/[0.06] p-8 sm:p-12"
        >
          <h2 className="max-w-2xl font-display text-2xl font-black uppercase leading-tight tracking-tight text-white sm:text-3xl">
            Susține proiectele fără să te coste nimic
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-neutral-300">
            Redirecționează până la 3,5% din impozitul pe venit către asociație
            sau, dacă ai o firmă, sponsorizează un proiect din impozitul pe
            profit. Îți dăm toate documentele precompletate.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/redirectioneaza-3-5"
              data-testid="transparency-cta-redirect"
              className="rounded-full bg-neon px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white"
            >
              Redirecționează 3,5%
            </Link>
            <Link
              to="/contact"
              data-testid="transparency-cta-contact"
              className="rounded-full border border-white/20 px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-neutral-200 transition-colors hover:border-neon hover:text-neon"
            >
              Scrie-ne
            </Link>
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
            Asociația Art &amp; Outdoor România · CIF 38948024 · IBAN RO33 RNCB 0003 1859 9467 0001
          </p>
        </div>
      </FadeUp>
    </div>
  );
}
